import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { getServiceListingTypes, serviceSearchQuery } from '../../../../util/serviceSearch';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import { getAriaCurrent } from '../currentNavItem';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

// Identifies the hosted "Найти задачи" link by its exact query contract.
// Matching is intentionally on the query parameter (not path, label or index)
// so hosted-config wording or ordering changes don't break detection.
const isTaskSearchLink = link =>
  typeof link?.href === 'string' && link.href.includes('pub_listingType=tasks');

// Persists across component remounts (SPA navigations) so that the
// signup/login links are not hidden on every route change.
// Starts as false on initial server render, becomes true after first mount.
let hasHydratedOnce = false;

// A topbar link to a named route with the current-page indicator (backlog #9): aria-current and the
// indigo underline (D28: indigo = "you are here").
const CurrentAwareLink = ({ currentNav, className, name, params, children, ...rest }) => {
  const ariaCurrent = getAriaCurrent({ name, params }, currentNav);
  return (
    <NamedLink
      {...rest}
      name={name}
      params={params}
      className={classNames(className, { [css.currentLink]: ariaCurrent })}
      aria-current={ariaCurrent}
    >
      {children}
    </NamedLink>
  );
};

const SignupLink = ({ currentNav }) => {
  return (
    <CurrentAwareLink
      id="signup-link"
      name="SignupPage"
      className={css.topbarLink}
      currentNav={currentNav}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </CurrentAwareLink>
  );
};

const LoginLink = ({ currentNav }) => {
  return (
    <CurrentAwareLink
      id="login-link"
      name="LoginPage"
      className={css.topbarLink}
      currentNav={currentNav}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </CurrentAwareLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab, currentNav }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <CurrentAwareLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: inboxTab }}
      currentNav={currentNav}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </CurrentAwareLink>
  );
};

const ProfileMenu = ({
  currentNav,
  currentUser,
  onLogout,
  showManageListingsLink,
  manageListingsLabelId,
  intl,
}) => {
  const ariaCurrent = page => getAriaCurrent({ name: page }, currentNav);
  const itemProps = page => ({
    name: page,
    className: classNames(css.menuLink, { [css.currentPage]: ariaCurrent(page) }),
    'aria-current': ariaCurrent(page),
  });
  const itemPages = [
    ...(showManageListingsLink ? ['ManageListingsPage'] : []),
    'ProfileSettingsPage',
    'AccountSettingsPage',
  ];
  // Q4: the avatar is underlined when the open page is inside its menu (the item carries aria-current).
  const hasCurrentItem = itemPages.some(ariaCurrent);

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={classNames(css.profileMenuLabel, { [css.currentLink]: hasCurrentItem })}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink {...itemProps('ManageListingsPage')}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id={manageListingsLabelId} />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink {...itemProps('ProfileSettingsPage')}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink {...itemProps('AccountSettingsPage')}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {Object} props.currentNav the open page (currentNavItem.getCurrentNav)
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentNav,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    forceKeywordSearch = false,
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
  } = props;
  const [mounted, setMounted] = useState(hasHydratedOnce);

  useEffect(() => {
    hasHydratedOnce = true;
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const isCustomer = publicData.userType === 'customer';
  const isProvider = publicData.userType === 'provider';
  const shouldShowPostListing = isAuthenticated;
  // Inline "Создать задачу" text action is client-only; the provider's create
  // action is the filled main button instead (D28).
  const showInlineCreateListing = isAuthenticated && isCustomer;
  const postListingLabelId = isProvider ? 'TopbarDesktop.createListing' : 'TopbarDesktop.createTask';
  const manageListingsLabelId = isCustomer
    ? 'TopbarDesktop.yourListingsLinkClient'
    : isProvider
      ? 'TopbarDesktop.yourListingsLinkProvider'
      : 'TopbarDesktop.yourListingsLinkProvider';

  // Desktop-only recomposition of the hosted custom links. The original
  // customLinks array (and each link object) is left untouched so the mobile
  // menu keeps its source order.
  const sourceCustomLinks = Array.isArray(customLinks) ? customLinks : [];
  // Task-search link ("Найти задачи") promoted to a semibold secondary action.
  const taskSearchLink = sourceCustomLinks.find(isTaskSearchLink);
  // Remaining hosted links (e.g. "О нас", "Блог") in their original order.
  const nonTaskLinks = sourceCustomLinks.filter(link => !isTaskSearchLink(link));
  const secondaryTaskLinkMaybe = taskSearchLink
    ? [{ ...taskSearchLink, secondaryCta: true }]
    : [];
  // Local desktop-only ordinary link for the categories directory.
  const categoriesLink = {
    group: 'primary',
    text: intl.formatMessage({ id: 'TopbarDesktop.viewAllCategories' }),
    type: 'internal',
    route: {
      name: 'LandingPage',
      to: { hash: '#all-categories' },
    },
  };
  // Role-specific measured (desktop) link order:
  // - Provider: Найти задачи (secondary) -> О нас -> Блог (no Каталог услуг).
  // - Client: Каталог услуг -> О нас -> Блог (Создать задачу is injected first
  //   by CustomLinksMenu; no Найти задачи).
  // - Guest: Найти задачи (secondary) -> Каталог услуг -> О нас -> Блог.
  // - Authenticated but userType not yet resolved: Каталог услуг -> О нас ->
  //   Блог (no create/secondary action, no Найти задачи).
  // The auth state is read through `mounted` (authenticatedOnClientSide): the server renders
  // every page signed out, so the hydrating render must produce the guest set too (backlog #4, H1-a).
  const desktopCustomLinks = isProvider
    ? [...secondaryTaskLinkMaybe, ...nonTaskLinks]
    : isCustomer
      ? [categoriesLink, ...nonTaskLinks]
      : authenticatedOnClientSide
        ? [categoriesLink, ...nonTaskLinks]
        : [...secondaryTaskLinkMaybe, categoriesLink, ...nonTaskLinks];

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} currentNav={currentNav} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentNav={currentNav}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={shouldShowPostListing}
      manageListingsLabelId={manageListingsLabelId}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <SignupLink currentNav={currentNav} />
  );
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink currentNav={currentNav} />;

  // Role-based main button rendered in the fixed CTA slot: an indigo fill for
  // every role (D28), always directly visible across the whole desktop range
  // (uses only the common .primaryCta base, no visibility breakpoint).
  // - Provider: "Создать услугу" (NewListingPage).
  // - Guest / client / authenticated-pending: "Все услуги" (SearchPage,
  //   service listing types of the runtime config, tasks excluded; D22, T15). On the service search
  //   it carries aria-current only: a filled button is an action, no visual mark (backlog #9, Q2).
  const findServicesTo = { search: serviceSearchQuery(getServiceListingTypes(config)) };
  const primaryCta = isProvider ? (
    <NamedLink name="NewListingPage" className={css.primaryCta}>
      <span className={css.primaryCtaLabel}>
        <FormattedMessage id="TopbarDesktop.createListing" />
      </span>
    </NamedLink>
  ) : (
    <NamedLink
      name="SearchPage"
      to={findServicesTo}
      className={css.primaryCta}
      aria-current={getAriaCurrent({ name: 'SearchPage', to: findServicesTo }, currentNav)}
    >
      <span className={css.primaryCtaLabel}>
        <FormattedMessage id="TopbarDesktop.findServices" />
      </span>
    </NamedLink>
  );

  // Stable key so the measured CustomLinksMenu remounts (and re-measures)
  // whenever the role, auth state, inline-create flag or link composition
  // changes, avoiding stale measured links after an SPA role/auth transition.
  const roleKey = !authenticatedOnClientSide
    ? 'guest'
    : isCustomer
      ? 'customer'
      : isProvider
        ? 'provider'
        : 'authPending';
  const customLinksMenuKey = `${roleKey}:${showInlineCreateListing ? '1' : '0'}:${desktopCustomLinks
    .map(
      l =>
        `${l.text}|${l.href || ''}|${l.route?.name || ''}|${l.route?.to?.hash || ''}|${l.route?.to
          ?.search || ''}|${l.secondaryCta ? 's' : ''}`
    )
    .join('~')}`;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
      forceKeywordSearch={forceKeywordSearch}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <div className={css.navContainer}>
        <LinkedLogo
          id="logo-topbar-desktop"
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {searchFormMaybe}

        <CustomLinksMenu
          key={customLinksMenuKey}
          currentNav={currentNav}
          customLinks={desktopCustomLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
          showCreateListingsLink={showInlineCreateListing}
          postListingLabelId={postListingLabelId}
          categoriesCta={primaryCta}
        />

        {inboxLinkMaybe}
        {profileMenuMaybe}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </div>
    </nav>
  );
};

export default TopbarDesktop;
